import { TwContainer } from '~/lib/TwContainer';
import { Carousel } from '@mantine/carousel';
import { useRef } from 'react';
import Autoplay from 'embla-carousel-autoplay';
import { Card, Image, Stack, Text, Title } from '@mantine/core';
import { useMediaQuery } from '@mantine/hooks';

const comments = [
	{
		name: 'Sonny Bellina',
		company: 'Executive Wash and Detail',
		position: 'CEO & Owner',
		img: '/images/ewd.png',
		comment: (
			<>
				Working with Champtek has been one of the best business decisions we've made. Our carwash and detailing operations were growing, but we were
				struggling to keep everything streamlined—from appointments to inventory tracking. The custom ERP system Champtek built has completely
				transformed our workflow. It has automated our scheduling, enhanced customer tracking, and even improved team communication. Their team took
				the time to understand our unique processes and built features that are tailor-made for us. The UI is intuitive, and even our non-technical
				staff picked it up in no time. We also appreciated their post-deployment support; they were always available for updates and tweaks. Now,
				we’re operating more efficiently, serving more customers, and saving both time and money. I’m excited to continue this partnership and have
				already referred two other businesses to Champtek. They truly understand service-driven industries.
			</>
		),
	},
	{
		name: 'Adolfo Lorenzo',
		company: 'Wellview Care',
		position: 'CEO',
		img: '/images/wv.png',
		comment: (
			<>
				As a healthcare provider with multiple facilities, having a unified, reliable, and HIPAA-compliant EMR solution is non-negotiable. Champtek
				came in with a deep understanding of the healthcare space and built a system that has brought consistency and clarity across all four of our
				clinics. Their EMR platform allows our doctors and admin staff to securely manage patient records, appointment scheduling, lab results, and
				billing—all in one place. The learning curve for our staff was minimal due to the clean UI and well-structured training they provided. We also
				integrated their reporting tools to get better insights into patient flow and operational efficiency. The team is proactive, responsive, and
				always ready to innovate with us. Since implementing their solution, patient turnaround time has improved, and we’ve reduced administrative
				workload significantly. Champtek has become an essential technology partner, and we’re planning new upgrades with them in the near future.
			</>
		),
	},
	{
		name: 'Cesar Estevez',
		company: 'Beneficial Ownership Information Service',
		position: 'Owner',
		img: '/images/boir.png',
		comment: (
			<>
				Launching an e-commerce service to assist users with BOI (Beneficial Ownership Information) filings is a complex process. We needed a partner
				who not only understood web development but also compliance and security requirements from FinCEN. Champtek delivered everything we needed—and
				more. They built a secure and scalable platform that allows users to confidently complete and submit their filings. The backend is rock-solid,
				with strong encryption and user authentication. The frontend is clean, intuitive, and optimized for conversions. Their team also provided
				detailed admin dashboards for internal tracking and document management. What impressed us most was their ability to translate government
				filing workflows into something the average user can easily understand. They've stayed in close communication post-launch, handling updates
				and customer support features quickly. We trust them completely with our platform and are excited to expand our offerings with their continued
				help.
			</>
		),
	},
	{
		name: 'Roberto Ramos',
		company: 'Labor Poster Service',
		position: 'Owner',
		img: '/images/lps.png',
		comment: (
			<>
				We came to Champtek with a vision: to build a reliable, user-friendly e-commerce site that sells customized labor law posters, automatically
				generated per state and industry compliance. Not only did they meet our expectations, but they exceeded them. From the moment we started
				collaborating, their team was engaged, thoughtful, and deeply knowledgeable. They created a responsive website with dynamic product
				customization features, integrated payment gateways, and automated email notifications for order tracking and updates. They even built an
				internal system to help us manage compliance data updates across all U.S. states, which was a huge operational challenge before. We’ve noticed
				a measurable increase in sales and customer retention thanks to the smooth and professional user experience. Champtek has truly helped bring
				our business into the digital age, and we’re grateful for their continued support and forward-thinking approach. We consider them more than a
				vendor—they’re a trusted tech partner.
			</>
		),
	},
	{
		name: 'Geraldo Rodriguez',
		company: 'Nabe Pharmacy',
		position: 'CEO & Owner',
		img: '/images/npx.png',
		comment: (
			<>
				Running a pharmacy in today’s regulatory environment requires a robust and compliant software infrastructure. We approached Champtek with the
				need for a comprehensive CMR (Customer Medication Record) and pharmacy management system that could not only handle prescriptions but also
				inventory, insurance processing, and customer engagement. What they delivered was outstanding. Their custom-built solution integrates all
				aspects of our workflow—from prescription entry to refill alerts and patient communication. It’s compliant, fast, and simple enough for our
				busy staff to use daily. They've also integrated tools that help us track controlled substances and flag potential drug interactions, ensuring
				patient safety. One of the biggest wins was their real-time reporting module, which helps us maintain compliance with state boards and
				identify areas for operational improvement. We’re currently in talks to expand the system to support home delivery tracking. Champtek’s
				dedication, technical excellence, and healthcare understanding make them a standout tech partner we’re proud to recommend.
			</>
		),
	},
];
export const SectionSix = () => {
	const autoplay = useRef(Autoplay({ delay: 1000 * 30 }));
	const isMobile = useMediaQuery('(max-width: 768px)');
	return (
		<section className="bg-white">
			<TwContainer className="py-20 md:px-8 px-2 grid gap-12">
				<div className={'col-span-full flex flex-col mx-auto  text-center gap-2'}>
					<h1 className="font-semibold text-primary text-5xl max-w-3xl font-saira">Hear it from our clients</h1>
					<div className="max-w-2xl text-gray-700 font-inter">
						We are very proud of our customer satisfaction score. Have a listen to hear what some of our clients have to say about us:
					</div>
				</div>
				<Carousel
					slideSize={isMobile ? '100%' : '80%'}
					withIndicators
					slideGap={'md'}
					emblaOptions={
						isMobile
							? undefined
							: {
									loop: true,
									dragFree: false,
									align: 'center',
								}
					}
					plugins={[autoplay.current]}
					onMouseEnter={autoplay.current.stop}
					onMouseLeave={() => autoplay.current.play()}
					withControls>
					{comments.map((comment, i) => (
						<Carousel.Slide key={i}>
							<Card className={'bg-secondary h-full flex-row flex gap-6 p-8'}>
								<Image
									className={'hidden md:block'}
									style={{ filter: 'brightness(0) invert(1)' }}
									maw={180}
									fit={'contain'}
									radius={'lg'}
									src={comment.img || ''}
								/>
								<Stack gap={0}>
									<Text
										fz={isMobile ? 14 : 18}
										c={'white'}>
										{comment.comment}
									</Text>
									<Title
										mt={'lg'}
										order={4}
										c={'white'}>
										{comment.name}
									</Title>
									<Title
										order={5}
										c={'dimmed'}>
										{comment.position}, {comment.company}
									</Title>
								</Stack>
							</Card>
						</Carousel.Slide>
					))}
				</Carousel>
			</TwContainer>
		</section>
	);
};
